"use client";

// THE LINK, WHOLE, ON ONE LINE. A skill's url is the interface, so it never
// breaks across two rows: where it fits it sits still, and where it doesn't it
// drifts past like a ticker (two copies, one seamless loop) behind a soft edge
// fade. Under prefers-reduced-motion it truncates instead. Hover or focus
// pauses the drift so the link can be read or selected.

import { useCallback, useEffect, useRef, useState } from "react";
import { DM_Mono } from "next/font/google";
import "./linkbar.css";

// the link bar speaks DM Mono (Figma 2843:5456) — its own face, loaded here only
const dmMono = DM_Mono({ subsets: ["latin"], weight: "400", display: "swap" });

export default function PasteUrl({ url }: { url: string }) {
  const ref = useRef<HTMLElement>(null);
  const [over, setOver] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const first = el.querySelector<HTMLElement>("[data-url]");
      setOver(first ? first.offsetWidth > el.clientWidth + 1 : false);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [url]);

  return (
    <code
      ref={ref}
      className="sk-paste-url"
      data-ticker={over || undefined}
      title={url}
      style={{ ["--ticker-dur" as string]: `${Math.max(12, url.length * 0.3)}s` }}
    >
      <span className="sk-paste-url__run">
        <span data-url>{url}</span>
        {over && <span aria-hidden>{url}</span>}
      </span>
    </code>
  );
}

/** THE COPY DISC — one round button in the organism's deep ink, seated inside
 *  the link field's right edge. The icon settles into a check when it lands;
 *  the word lives in the label for a screen reader (aria-live says "copied"). */
export function PasteCopy({ done, onClick, noswipe }: { done: boolean; onClick: () => void; noswipe?: boolean }) {
  return (
    <button
      type="button"
      className="sk-paste-copy"
      data-done={done || undefined}
      data-noswipe={noswipe || undefined}
      onClick={onClick}
      aria-live="polite"
    >
      {done ? (
        <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
          <rect x="8" y="8" width="12" height="12" rx="2.5" />
          <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
        </svg>
      )}
      <span className="sk-paste-copy__label">{done ? "copied" : "copy link"}</span>
    </button>
  );
}

// ── THE LINK BAR (Figma 2843:5455, 2026-09-29; slimmed 2026-10-19) ───────────
// 2026-10-19 (John): the pill a little lower, the copy disc smaller and sleeker,
// and the words one plain light white. They no longer fade across their whole
// length: a path that fits is read whole, and only a path that runs under the
// disc fades, by a MASK just before it (`data-over`), never a colour laid over
// the words. One bar, everywhere a link is handed over.
// Replaces the near-white pill of 2795:5369. A pane of DARK GLASS: the ground
// seen through a black wash (26% on a night room, deepening on a pale one so the
// white words keep their contrast), a hairline of white at 16%, one faint white
// lift underneath. Inside it netti's mark and the link's PATH in DM Mono, white,
// fading from full to a quarter across its length, so a long path dissolves
// instead of being cut. At its right edge the copy disc: the innernet CELL in
// white, a whisper of gradient, a white rim at 21% and a soft white glow, the
// two-squares mark in black. It settles to a check when the link has landed.
//
// The whole url is what is copied; only its path is shown, because the origin
// is the same on every bar and the path is the part that says what this is.

const NETTI_MARK = "M7.72488 1.70809C7.48758 0.499797 7.71293 -0.535278 9.28593 0.305522C10.4614 0.934599 11.549 2.183 12.3251 3.2109C12.851 1.85869 14.1785 -0.285031 15.3902 2.14697C16.1126 3.59683 15.8375 5.81254 15.2072 7.28501C15.9117 7.18016 16.4918 7.0439 16.9948 7.61232C17.1253 9.12437 14.775 10.8256 13.6213 11.6196C15.0515 12.1079 17.302 13.3398 14.7574 14.4998C13.2061 15.207 10.8744 14.9135 9.28602 14.3431C9.35229 14.729 9.48962 15.6963 9.08693 15.8941C7.65182 16.5988 5.26039 13.5825 4.68454 12.846C4.17882 14.1452 2.88802 16.3115 1.64643 13.9535C0.869954 12.479 1.18826 10.2674 1.80108 8.75227C1.09883 8.88795 0.461201 8.99744 0.00213707 8.39098C-0.0801505 6.88459 2.23451 5.2446 3.38574 4.41817C3.2533 4.37397 3.12135 4.32817 2.99006 4.28088C1.44061 3.7253 0.0552364 2.46105 2.28732 1.52824C3.88535 0.860616 6.12418 1.10369 7.72488 1.70809ZM8.8533 5.59515C8.6689 5.5863 8.42904 5.57796 8.24659 5.59111C7.33104 5.62725 6.30666 5.79699 5.64897 6.47651C5.19993 6.94046 5.05399 7.54941 5.08059 8.17189C5.10545 8.7535 5.30876 9.29095 5.75778 9.69252C6.39863 10.2657 7.33682 10.4357 8.1814 10.4693C8.35253 10.4761 8.55261 10.4794 8.72468 10.4726C8.72988 10.4724 8.73514 10.4721 8.74033 10.4718C9.65702 10.4342 10.6742 10.2661 11.3329 9.58421C11.7786 9.12294 11.9269 8.50754 11.8986 7.88934C11.8768 7.3145 11.6623 6.75901 11.2174 6.36477C10.5869 5.80625 9.68192 5.63492 8.8533 5.59515Z";

/** the path a bar shows: everything after the origin (the whole thing when it has none) */
export function pathOf(url: string): string {
  const m = /^[a-z]+:\/\/[^/]+(\/.*)?$/i.exec(url.trim());
  return m ? (m[1] || "/") : url;
}

/** the words' ref: say whether they run past their room (then, and only then,
 *  they fade out just before the disc). Written straight onto the element. */
function useOverflowEdge() {
  const off = useRef<(() => void) | null>(null);
  return useCallback((el: HTMLElement | null) => {
    off.current?.();
    off.current = null;
    if (!el) return;
    const measure = () => el.toggleAttribute("data-over", el.scrollWidth > el.clientWidth + 1);
    measure();
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    // the mono face swaps in after first paint and changes every width
    let alive = true;
    if (typeof document !== "undefined" && "fonts" in document) document.fonts.ready.then(() => { if (alive) measure(); }).catch(() => {});
    off.current = () => { alive = false; ro?.disconnect(); };
  }, []);
}

export function LinkBar({
  url,
  done,
  onCopy,
  noswipe,
  label,
  flat,
  size = "regular",
  shown,
}: {
  url: string;
  done: boolean;
  onCopy: () => void;
  noswipe?: boolean;
  /** what the copy button says to a screen reader (default: copy link) */
  label?: string;
  /** no gradient in the disc, no fade on the words (the developers page). The
   *  default is unchanged for every other room. */
  flat?: boolean;
  /** compact: smaller type and height, and a long value (a key, an endpoint) wraps
   *  inside the bar instead of being cut, so the whole of it can be read. For a sheet
   *  (law_one_sheet_size); regular is every other room's bar, unchanged. */
  size?: "regular" | "compact";
  /** the words on the bar when they are not the path of `url` (an endpoint shows its
   *  host; what is copied is still `url`) */
  shown?: string;
}) {
  const edge = useOverflowEdge();
  return (
    <div
      className={`lbar ${dmMono.className}${flat ? " lbar--flat" : ""}${size === "compact" ? " lbar--compact" : ""}`}
      data-noswipe={noswipe || undefined}
    >
      <span className="lbar__line" title={url}>
        <svg className="lbar__mark" width="17" height="16" viewBox="0 0 17 16" aria-hidden>
          <path fillRule="evenodd" clipRule="evenodd" d={NETTI_MARK} fill="currentColor" />
        </svg>
        <span ref={size === "compact" ? undefined : edge} className="lbar__path">{shown ?? pathOf(url)}</span>
      </span>
      <button
        type="button"
        className="lbar__copy"
        data-done={done || undefined}
        data-noswipe={noswipe || undefined}
        onClick={onCopy}
        aria-live="polite"
        aria-label={done ? "copied" : label ?? "copy link"}
      >
        <svg className="lbar__cell" viewBox="15.5 14.5 55.4 43.2" preserveAspectRatio="none" aria-hidden>
          {flat ? null : (
            <defs>
              <linearGradient id="lbar-cell-g" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fff" stopOpacity="0.9" />
                <stop offset="1" stopColor="#fff" />
              </linearGradient>
            </defs>
          )}
          <path d="M41.2436 15.0589C42.6971 14.9457 44.6079 15.0171 46.0769 15.0933C52.6774 15.4354 59.8864 16.9093 64.9082 21.7142C68.4529 25.1059 70.1614 29.885 70.3351 34.8305C70.5601 40.1488 69.379 45.4427 65.8292 49.4109C60.582 55.2773 52.4788 56.7237 45.1765 57.0474C45.1351 57.0498 45.0938 57.0522 45.0524 57.0542C43.6817 57.1126 42.0881 57.0846 40.7248 57.0261C33.9969 56.7368 26.5231 55.2739 21.4182 50.3428C17.8414 46.8881 16.222 42.2648 16.024 37.2613C15.8121 31.906 16.9747 26.6674 20.5517 22.6759C25.7907 16.8299 33.9505 15.3698 41.2436 15.0589Z" fill={flat ? "#fff" : "url(#lbar-cell-g)"} stroke="#fff" strokeOpacity="0.21" />
        </svg>
        {done ? (
          <svg className="lbar__icon" width="22" height="22" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg className="lbar__icon" width="22" height="21" viewBox="31.3 24.6 23 21.9" fill="none" aria-hidden>
            <path d="M44.5277 25.2692C47.0484 25.2692 49.0919 27.3139 49.0921 29.8346V30.9753H46.8099V29.8346C46.8096 28.5744 45.788 27.5524 44.5277 27.5524H36.5404C35.2802 27.5526 34.2584 28.5745 34.2581 29.8346V36.6813C34.2583 37.9415 35.2802 38.9633 36.5404 38.9635H37.681V41.2458H36.5404C34.0197 41.2455 31.9751 39.202 31.9749 36.6813V29.8346C31.9752 27.314 34.0198 25.2694 36.5404 25.2692H44.5277Z" fill="currentColor" />
            <rect x="38.8226" y="32.117" width="14.8348" height="13.6937" rx="3.42342" stroke="currentColor" strokeWidth="2.28228" />
          </svg>
        )}
      </button>
    </div>
  );
}
